import { useOutsideClick } from '@/hooks';
import { MouseEvent, useCallback, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { ChevronLeft } from '../Icons';

type DropdownKey = string | number;

export type DropdownOption<TValue extends DropdownKey> = {
  label: string;
  value: TValue;
};

type DropdownArrowProps<TValue extends DropdownKey> = {
  placeholder: string;
  selectedValue?: TValue;
  options: DropdownOption<TValue>[];
  onSelect: (value: TValue) => void;
  className?: string;
};

export const DropdownArrow = <TValue extends DropdownKey>({
  selectedValue,
  options,
  placeholder,
  onSelect,
  className,
}: DropdownArrowProps<TValue>) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  const onOutsideClick = useCallback(() => {
    setIsOpen(false);
  }, []);

  useOutsideClick(ref, onOutsideClick, isOpen);

  const handleOnSelect = useCallback(
    (event: MouseEvent, value: TValue) => {
      event.stopPropagation();
      setIsOpen(false);
      onSelect(value);
    },
    [onSelect],
  );

  const selectedLabel = useMemo(
    () => options.find((option) => option.value === selectedValue)?.label,
    [options, selectedValue],
  );

  return (
    <button
      ref={ref}
      className={twMerge(
        'relative min-w-32 text-sm font-normal',
        isOpen && 'rounded-b-none',
        className,
      )}
      onClick={() => setIsOpen((prev) => !prev)}
    >
      <div className="flex items-center justify-between gap-1 rounded-md bg-brightGray px-2 py-2">
        <span className={twMerge(!selectedLabel && 'text-spartanBlue')}>
          {selectedLabel || placeholder}
        </span>
        <ChevronLeft
          className={twMerge(
            ' size-3 -rotate-90 text-blueNuit transition-transform',
            isOpen && 'rotate-90',
          )}
        />
      </div>
      {isOpen && (
        <ul className="absolute z-10 flex max-h-36 w-full flex-1 flex-col overflow-auto rounded-b-md bg-brightGray">
          {options.map((option) => (
            <li
              key={option.value}
              className={twMerge(
                'p-2 text-left text-spartanBlue transition-colors hover:bg-rinseGray active:bg-trappedDarkness active:text-white',
                option.value === selectedValue &&
                  'bg-trappedDarkness text-white',
              )}
              onClick={(event) => {
                event.stopPropagation();
                handleOnSelect(event, option.value);
              }}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </button>
  );
};
